import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

// â"€â"€ Hint definitions â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const HINTS = [
  {
    key: "kpi_strip",
    label: "KPI STRIP",
    body: "Track your balance, P&L, win rate, and best session in real time. All metrics update automatically as you log trades.",
    position: "top-right" as const,
  },
  {
    key: "equity_curve",
    label: "EQUITY CURVE",
    body: "Your equity curve builds as you log trades. The more sessions you record, the clearer your edge becomes over time.",
    position: "top-right" as const,
  },
  {
    key: "agent_alerts",
    label: "AI AGENT FEED",
    body: "Your 7 AI agents post live insights here -- coaching notes, pattern alerts, risk warnings, and market context tailored to your trades.",
    position: "bottom-right" as const,
  },
  {
    key: "sidebar",
    label: "NAVIGATION",
    body: "Use the sidebar to navigate the platform. Hover over it to expand and reveal all page labels. Click any icon to navigate.",
    position: "left" as const,
  },
];

// â"€â"€ Props â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

interface Props {
  userId: string;
  onAllDismissed: () => void;
}

// â"€â"€ Component â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

export function OnboardingHints({ userId, onAllDismissed }: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    supabase
      .from("onboarding_hints")
      .select("hint_key")
      .eq("user_id", userId)
      .then(({ data }) => {
        if (data) setDismissed(new Set(data.map((r) => r.hint_key)));
        setLoaded(true);
      });
  }, [userId]);

  const dismiss = async (key: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(key);
      if (next.size === HINTS.length) {
        supabase
          .from("user_profiles")
          .upsert({ user_id: userId, hints_dismissed: true }, { onConflict: "user_id" })
          .then(() => onAllDismissed());
      }
      return next;
    });

    await supabase.from("onboarding_hints").upsert(
      { user_id: userId, hint_key: key, dismissed_at: new Date().toISOString() },
      { onConflict: "user_id,hint_key" }
    );
  };

  if (!loaded) return null;

  const visible = HINTS.filter((h) => !dismissed.has(h.key));
  if (visible.length === 0) return null;

  const topRight    = visible.filter((h) => h.position === "top-right");
  const bottomRight = visible.filter((h) => h.position === "bottom-right");
  const left        = visible.filter((h) => h.position === "left");

  return (
    <>
      {topRight.length > 0 && (
        <div className="fixed top-20 right-4 z-[90] flex flex-col gap-2" style={{ pointerEvents: "none" }}>
          {topRight.map((hint) => (
            <HintCard key={hint.key} hint={hint} onDismiss={dismiss} />
          ))}
        </div>
      )}

      {bottomRight.length > 0 && (
        <div className="fixed bottom-6 right-4 z-[90] flex flex-col gap-2" style={{ pointerEvents: "none" }}>
          {bottomRight.map((hint) => (
            <HintCard key={hint.key} hint={hint} onDismiss={dismiss} />
          ))}
        </div>
      )}

      {left.length > 0 && (
        <div className="fixed top-1/2 left-14 -translate-y-1/2 z-[90] flex flex-col gap-2" style={{ pointerEvents: "none" }}>
          {left.map((hint) => (
            <HintCard key={hint.key} hint={hint} onDismiss={dismiss} />
          ))}
        </div>
      )}
    </>
  );
}

// â"€â"€ Single hint card â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

function HintCard({
  hint,
  onDismiss,
}: {
  hint: typeof HINTS[0];
  onDismiss: (key: string) => void;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      style={{
        pointerEvents: "auto",
        width: 256,
        border: "1px solid rgba(29,158,117,0.3)",
        background: "rgba(0,0,0,0.98)",
        padding: 12,
        transition: "opacity 300ms, transform 300ms",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
        overflow: "visible",
      }}
    >
      <div className={cn("flex items-start justify-between gap-2 mb-2")}>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-[#1d9e75] animate-pulse shrink-0" style={{ display: "inline-block" }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#1d9e75]">
            {hint.label}
          </span>
        </div>
        <button
          onClick={() => onDismiss(hint.key)}
          className="text-white/25 hover:text-white/60 transition-colors shrink-0"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 1 }}
        >
          <X size={12} />
        </button>
      </div>
      <p className="text-[11px] leading-relaxed" style={{ color: "rgba(255,255,255,0.45)", margin: 0 }}>
        {hint.body}
      </p>
      <button
        onClick={() => onDismiss(hint.key)}
        className="text-[10px] hover:text-white/50 transition-colors uppercase tracking-[0.08em]"
        style={{ color: "rgba(255,255,255,0.25)", marginTop: 8, background: "none", border: "none", cursor: "pointer", padding: 0, display: "block" }}
      >
        Got it
      </button>
    </div>
  );
}
